/**
 * Drawer component - migrated from chariot-ui
 *
 * Migration reason: Close button positioning issue where X button overlaps drawer title
 *
 * Original: chariot-ui/Drawer
 * Migrated: 2025-10-20
 *
 * Changes from original:
 * - Fixed close button positioning from `-left-14` to `-left-20` to prevent overlap with drawer titles
 * - Added scrollResetKey prop for explicit scroll reset without remounting (2025-11-19)
 * - Generalized the Drawers container to MAX_OPEN_DRAWERS fixed slots (constant
 *   moved to state/drawer.constants.ts); cap enforcement moved into the
 *   drawerOrder write path in state/global.state.tsx; stacked widths applied
 *   via the initialWidth ladder (2026-07-31)
 * - Rewrote the Drawers container around a single shared AnimatePresence with
 *   an identity-key contract (AppDrawers keys each Drawer by drawer type):
 *   drawer instances survive stack shifts when a lower drawer closes instead
 *   of leaking/losing state across index-keyed slots, and the closed drawer
 *   gets a real exit animation (2026-07-31)
 * - Focus-return slots keyed by identity: captureOpener stores the id from
 *   pushDrawerSlot() and restoreFocus pops exactly that id, so a non-top close
 *   in the 3-deep stack can no longer delete the top drawer's saved slot
 *   (2026-07-31)
 * - Added the `uncounted` prop: the Drawers container extends its render slot
 *   count by the number of open uncounted children (slotCount =
 *   MAX_OPEN_DRAWERS + uncountedCount, stack = slice(-slotCount)), so an
 *   always-available overlay drawer (the NotificationsDrawer) opened at a
 *   full stack no longer unmounts the bottom counted drawer;
 *   STACKED_DRAWER_WIDTHS gains slot 3 to keep the left-edge stagger for the
 *   4-deep stack (2026-07-31)
 * - Escape ownership gated on useIsPresent(): a drawer exiting the shared
 *   AnimatePresence keeps its cached escapeKey/open props for the exit
 *   animation, so presence is what hands Esc off to the promoted top drawer
 *   (2026-08-01)
 * - Maintained all other functionality and styling
 */

import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  popDrawerSlot,
  pushDrawerSlot,
  useDrawerEscape,
} from '@/hooks/hotkeys';
import { MAX_OPEN_DRAWERS } from '@/state/drawer.constants';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'parent' | 'child';
  escapeKey?: boolean;
  uuid?: string;
  scrollResetKey?: string; // When this changes, reset scroll to top
  initialWidth?: string; // Optional override for default width (e.g., '80%', '1200px')
  // An always-available overlay drawer (e.g. the NotificationsDrawer) that is
  // exempt from the MAX_OPEN_DRAWERS state cap and must not consume one of
  // the container's render slots — `Drawers` grants each open uncounted child
  // its own extra slot instead of unmounting a counted drawer.
  uncounted?: boolean;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  className,
  style,
  variant = 'parent',
  uuid: _uuid = '', // Kept for API compatibility, unused after jiggle removal
  escapeKey = false,
  scrollResetKey,
  initialWidth,
}) => {
  const isParent = variant === 'parent';
  const defaultWidth = isParent ? '60%' : 'calc(61% - 60px)';

  const [width, setWidth] = useState<string>(initialWidth ?? defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const zIndex = isParent ? 50 : 51;

  // The element that had focus when this drawer opened (the "opener" — expected
  // to be the table scroll container the user `→`-entered from). Captured ONLY
  // if it sits OUTSIDE this drawer, so we never restore focus back into a
  // closing drawer. `wasOpenRef` makes restoration fire exactly once per close.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  // Identity of THIS drawer's focus-return slot (from `pushDrawerSlot()`), so
  // `restoreFocus` pops exactly the slot this instance owns — a non-top close
  // in the stacked-drawer case must not touch another drawer's snapshot.
  const slotIdRef = useRef<number | null>(null);

  // Derive the drawer's width from its slot props. Unconditional (no
  // `variant === 'child' || !width` guard): with identity-stable keys a drawer
  // instance SURVIVES stack shifts, so its slot props (`variant`,
  // `initialWidth`) change while mounted — e.g. a child promoted to parent
  // must re-derive the parent default width. None of the deps change while
  // the drawer sits in the same slot, so a dragged width is safe there; a
  // stack shift intentionally re-derives it from the new slot's props.
  // Use setTimeout to defer setState, avoiding synchronous cascading renders.
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setWidth(initialWidth ?? defaultWidth), 0);
      return () => clearTimeout(timer);
    }
  }, [open, initialWidth, defaultWidth, variant]);

  // Handle dragging to resize the drawer
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const drawerElement = e.currentTarget.parentElement;
    if (!drawerElement) return;

    const currentWidth = drawerElement.getBoundingClientRect().width;
    const startX = e.pageX;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startX - e.pageX;
      const newWidth = Math.max(
        300,
        Math.min(window.innerWidth * 0.8, currentWidth + deltaX)
      );
      setWidth(`${newWidth.toString()}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const computedStyle = {
    ...style,
    width,
    zIndex,
  };

  // Reset scroll when scrollResetKey changes
  useEffect(() => {
    if (scrollResetKey !== undefined && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  // Context-aware Escape: if focus is inside this drawer's tablist AND a table
  // armed a return target (via `→`-enter), return focus to the table and keep
  // the drawer open; otherwise close. A SINGLE handler decides return-vs-close
  // (react-hotkeys-hook v5 has no handler ordering guarantee). Gated by the
  // `escapeKey` prop so only the topmost drawer owns Esc-close — plus
  // `isPresent`: during a shared-AnimatePresence exit a closed drawer stays
  // mounted with its CACHED props (`escapeKey`/`open` still true), so presence
  // is the only signal it no longer owns Esc. Without the gate, its handler
  // fires alongside the promoted top drawer's and its stray re-close reverts
  // that drawer's close via same-tick setSearchParams last-write-wins.
  // Outside any PresenceContext (bare renders in tests) useIsPresent() is
  // always true, so this degrades to the plain escapeKey && open gate.
  const isPresent = useIsPresent();
  useDrawerEscape(onClose, scrollContainerRef, {
    enabled: escapeKey && open && isPresent,
  });

  // WAI-ARIA dialog focus-restoration for the NORMAL close paths (✕ button,
  // backdrop click, Escape-that-closes, route/state change that unmounts the
  // drawer). The Esc-from-tabs return is handled separately by `useDrawerEscape`
  // + the `drawerFocusReturn` registry; this mirrors that for every other close
  // so the table's focus-gated arrow-key row navigation works again after the
  // drawer goes away (the browser strands focus on <body> when the focused
  // element unmounts).
  //
  // Restoration runs in the effect CLEANUP, never in an `open===false` effect
  // body: React fires an effect's cleanup before re-running it on a dep change,
  // so the single `return () => restoreFocus()` covers BOTH unmount (the path
  // every consumer takes — each renders this Drawer conditionally inside
  // <AnimatePresence>, see `Drawers` below and `GapDetailDrawer`, so closing it
  // unmounts the component) AND any `[open]` dep-change (covering the rare
  // consumer that keeps the Drawer mounted and merely toggles `open` to false).
  // A `wasOpenRef` once-guard makes a single close restore exactly once. We only
  // RECLAIM stranded focus (activeElement is null/body/non-element) — if the
  // user deliberately moved focus to another real element, we leave it alone.
  const captureOpener = () => {
    const active = document.activeElement;
    const container = scrollContainerRef.current;
    // Only capture an opener that is OUTSIDE this drawer; if focus is already
    // inside the drawer, leave opener null (never restore into a closing drawer).
    openerRef.current =
      active instanceof HTMLElement &&
      !(container && container.contains(active))
        ? active
        : null;
    wasOpenRef.current = true;

    // F5: claim a per-drawer focus-return slot on open (saving the outer
    // drawer's slot), so a CHILD drawer's later close pops only its OWN slot and
    // restores the PARENT's stored table return-target instead of wiping it.
    // The slot is identity-keyed: we keep the id and `restoreFocus` pops
    // exactly that id (same `wasOpenRef` once-guard governs both), so closes
    // in any order — including a non-top close in the 3-deep stack — only ever
    // touch this drawer's own snapshot.
    slotIdRef.current = pushDrawerSlot();
  };

  const restoreFocus = () => {
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;

    const opener = openerRef.current;
    openerRef.current = null;

    // F5: pop THIS drawer's own focus-return slot by identity. When this
    // drawer is top-of-stack, that restores the OUTER (parent) drawer's slot.
    // Replaces the old unconditional `clearReturnTarget()`, which wiped the
    // single global slot on EVERY close — so closing a CHILD drawer destroyed
    // the PARENT's stored table return-target and the next Esc in the parent
    // closed instead of returning focus to the table. Identity keying (not a
    // blind LIFO pop) means a NON-TOP close (each stacked drawer's ✕ stays
    // clickable on insights pages) removes only this drawer's snapshot and
    // leaves the live slot — owned by the still-open top drawer — untouched.
    // Paired 1:1 with `pushDrawerSlot()` in `captureOpener` via the
    // `wasOpenRef` once-guard, so it also runs on unmount (where the old
    // `if (!open)` effect body never did).
    if (slotIdRef.current !== null) {
      popDrawerSlot(slotIdRef.current);
      slotIdRef.current = null;
    }

    const active = document.activeElement;
    const stranded =
      active === null ||
      active === document.body ||
      !(active instanceof HTMLElement);

    if (opener && opener.isConnected && stranded) {
      opener.focus({ preventScroll: true });
    }
  };

  useEffect(() => {
    if (open) {
      captureOpener();
    }

    // Cleanup runs on unmount AND on every `[open]` dep-change. On unmount the
    // browser has stranded focus on <body>; reclaim it for the opener. On a
    // mounted true→false toggle this same cleanup fires before the re-run, so it
    // restores there too — no separate `open===false` branch needed.
    return () => {
      restoreFocus();
    };
  }, [open]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '20%' }}
      transition={{
        type: 'tween',
        duration: 0.2,
        ease: 'easeOut',
      }}
      className="pointer-events-none fixed inset-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={twMerge(
            'pointer-events-auto absolute inset-y-0 right-0',
            className,
            isDragging && 'select-none'
          )}
          style={computedStyle}
        >
          <div className="relative h-full">
            <div className="h-full">
              <div
                ref={scrollContainerRef}
                tabIndex={-1}
                className="bg-chariot-background-primary border-chariot-stroke h-full overflow-x-hidden overflow-y-scroll border-l-2 outline-none"
                data-testid="drawer-content"
              >
                <button
                  onClick={onClose}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-6 -left-[52px] flex size-10 items-center justify-center rounded-full border-2 transition-colors"
                  data-testid="drawer-close-button"
                >
                  <span className="sr-only">Close panel</span>
                  <X className="text-chariot-text-primary size-5" />
                </button>

                <div
                  onMouseDown={startResizing}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-1/2 -left-10 flex h-20 w-6 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 transition-colors"
                  role="separator"
                  aria-label="Resize drawer"
                >
                  <div className="flex flex-row gap-1 p-2">
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                  </div>
                </div>

                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface DrawersProps {
  children: React.ReactNode;
  disableBackdrop?: boolean;
}

// Widths for stacked (non-bottom) drawer slots. The left-edge step between
// adjacent drawers is `60px − 1vw` (≈47px at a 1280px viewport, ≈41px at
// 1920px, shrinking further on ultrawides — beyond ~2000px adjacent close
// buttons begin to overlap). Lower drawers stay VISIBLE but are NOT clickable
// while a backdrop is shown — the backdrop covers them and a click there
// closes the topmost drawer; the exposed slivers are directly interactive
// only when `disableBackdrop` is set (e.g. the insights page). Index 1's
// value equals the historical child default, so the 2-drawer visuals are
// identical to the previous fixed parent/child implementation. Slot 3 exists
// because an open `uncounted` child (the NotificationsDrawer) extends the
// render slot count past MAX_OPEN_DRAWERS.
const STACKED_DRAWER_WIDTHS: Record<number, string> = {
  1: 'calc(61% - 60px)',
  2: 'calc(62% - 120px)',
  3: 'calc(63% - 180px)',
};

export const Drawers: React.FC<DrawersProps> = ({
  children,
  disableBackdrop = false,
}) => {
  const childArray = React.Children.toArray(children);
  const openDrawers = childArray.filter(
    (child): child is React.ReactElement<DrawerProps> =>
      React.isValidElement<DrawerProps>(child) && child.props.open
  );

  // `uncounted` children (the NotificationsDrawer) are exempt from the
  // MAX_OPEN_DRAWERS state cap, so each open one extends the render slot
  // count by itself — a cap-full stack plus open notifications renders ALL
  // children instead of unmounting the bottom counted drawer (state loss +
  // focus scramble). G2, PR #7304 review.
  const uncountedCount = openDrawers.filter(
    child => child.props.uncounted
  ).length;
  const slotCount = MAX_OPEN_DRAWERS + uncountedCount;

  // Warn developers if attempting to open more drawers than supported.
  // Defensive backstop only: the cap is enforced in the drawerOrder write path
  // (state/global.state.tsx) and uncounted children get their own extra slot,
  // so this fires only for hand-crafted URLs with a too-long drawerOrder param.
  if (openDrawers.length > slotCount) {
    console.warn(
      `[Drawers] Attempted to render ${openDrawers.length} drawers, but only ${slotCount} are supported. ` +
        `Oldest ${openDrawers.length - slotCount} drawer(s) will be hidden. ` +
        `Consider closing drawers before opening new ones.`
    );
  }

  // FIFO drawer limit: only render the last slotCount drawers to prevent UI
  // clutter. If more are queued, the oldest drawers are dropped from
  // rendering — the application should close drawers before opening more.
  const stack = openDrawers.slice(-slotCount);
  const topIndex = stack.length - 1;

  // ONE shared <AnimatePresence> for the whole stack — this is load-bearing.
  // Invariants:
  // - Keys come from the CHILDREN and must be identity-stable (AppDrawers keys
  //   each Drawer by its drawer TYPE). AnimatePresence flattens its children
  //   into a single keyed array, so when a lower drawer closes, a surviving
  //   drawer is a keyed REORDER: React moves the SAME instance to its new
  //   stack position (local state — scroll, tabs — survives; width is the
  //   exception, re-derived from the new slot's props), and
  //   only the closed drawer exits, with a real exit animation. Index-keyed
  //   per-slot presences leaked/lost survivor state and played phantom exits.
  // - React.cloneElement preserves the child's own key; do NOT construct new
  //   `<Drawer {...props} key={...}>` elements here — a container-minted key
  //   would recreate the slot-identity bug this design removes.
  // - The backdrop (key="backdrop") sits in DOM order just before the topmost
  //   drawer, so it dims every lower drawer and sits under the topmost one.
  //   Clicking it closes the topmost drawer.
  // - DOM order is the stacking order: modern browsers create a stacking
  //   context for fixed-position elements (and framer-motion's transforms
  //   also do) — CSS 2.1 does not mandate it for `fixed` alone.
  const renderStackedDrawer = (
    child: React.ReactElement<DrawerProps>,
    index: number
  ) =>
    React.cloneElement(child, {
      variant: index === 0 ? 'parent' : 'child',
      escapeKey: index === topIndex,
      initialWidth:
        index === 0
          ? child.props.initialWidth
          : (child.props.initialWidth ?? STACKED_DRAWER_WIDTHS[index]),
    });

  return (
    <AnimatePresence>
      {stack
        .slice(0, -1)
        .map((child, index) => renderStackedDrawer(child, index))}
      {stack.length >= 2 && !disableBackdrop && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => stack[topIndex].props.onClose()}
          data-testid="drawers-backdrop"
          className="bg-chariot-shade/50 pointer-events-auto fixed inset-0"
        />
      )}
      {stack.length > 0 && renderStackedDrawer(stack[topIndex], topIndex)}
    </AnimatePresence>
  );
};
